import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Cargo, Cliente, ControleProducaoDetalhado, CriarHistoricoProducao, Empresa, Fornecedor, Funcionario, FuncionarioCargoProducao, FuncionarioHistoricoSalario, HistoricoProducao, VendaCliente, VendedorCadastro } from '../models/cadastro.model';
import { ConfigService } from '../core/config/config.service';

@Injectable({
  providedIn: 'root'
})
export class CadastroService {
  private readonly configService = inject(ConfigService);

  constructor(private http: HttpClient) { }

  getEmpresas(): Observable<Empresa[]> {
    return this.http.get<Empresa[]>(`${this.cadastroUrl}/empresa`);
  }

  getFornecedores(): Observable<Fornecedor[]> {
    return this.http.get<Fornecedor[]>(`${this.cadastroUrl}/fornecedor`);
  }

  getFornecedorById(id: number): Observable<Fornecedor> {
    return this.http.get<Fornecedor>(`${this.cadastroUrl}/fornecedor/${id}`);
  }

  createFornecedor(fornecedor: Fornecedor): Observable<Fornecedor> {
    return this.http.post<Fornecedor>(`${this.cadastroUrl}/fornecedor`, fornecedor);
  }

  updateFornecedor(fornecedor: Fornecedor): Observable<Fornecedor> {
    return this.http.put<Fornecedor>(`${this.cadastroUrl}/fornecedor`, fornecedor);
  }

  deleteFornecedor(id: number): Observable<void> {
    return this.http.delete<void>(`${this.cadastroUrl}/fornecedor/${id}`);
  }

  getClientes(): Observable<any> {
   
    return this.http.get<any>(`${this.cadastroUrl}/clientes`);
  }

  getClienteById(id: number): Observable<Cliente> {
    return this.http.get<Cliente>(`${this.cadastroUrl}/clientes/${id}`);
  }

  getIdClienteByTelefone(telefone: string): Observable<number> {
    const param = new HttpParams().set('telefone', telefone);
    return this.http.get<number>(`${this.cadastroUrl}/clientes/buscarPorTelefone`, { params: param });
  }

  createCliente(cliente: Cliente): Observable<Cliente> {
    return this.http.post<Cliente>(`${this.cadastroUrl}/clientes`, cliente);
  }

  updateCliente(cliente: Cliente): Observable<Cliente> {
    return this.http.put<Cliente>(`${this.cadastroUrl}/clientes`, cliente);
  }

  atualizarDocumentoCliente(id: number, cpfCnpj: string): Observable<Cliente> {
    return this.http.patch<Cliente>(`${this.cadastroUrl}/clientes/${id}/documento`, { cpfCnpj });
  }

  deleteCliente(id: number): Observable<void> {
    return this.http.delete<void>(`${this.cadastroUrl}/clientes/${id}`);
  }

  getVendasByCliente(id: number): Observable<VendaCliente[]> {
    return this.http.get<VendaCliente[]>(`${this.cadastroUrl}/clientes/${id}/vendas`);
  }

  private get cadastroUrl(): string {
    const baseUrl = this.configService.settings()?.apiUrl;
    return `${baseUrl}/cadastro`;
  }

  private get funcionarioUrl(): string {
    const baseUrl = this.configService.settings()?.apiUrl;
    return `${baseUrl}/funcionarios`;
  }

  getProdutosSimples(): Observable<any> {
    return this.http.get<any>(`${this.cadastroUrl}/produto`);
  }

  deleteProduto(id: number): Observable<void> {
    return this.http.delete<void>(`${this.cadastroUrl}/produto/${id}`);
  }

  getProdutoById(id: number): Observable<any> {
    return this.http.get<any>(`${this.cadastroUrl}/produto/${id}`);
  }

  createProduto(produto: any): Observable<any> {
    return this.http.post<any>(`${this.cadastroUrl}/produto`, produto);
  }

  updateProduto(produto: any, id: number): Observable<any> {
    return this.http.put<any>(this.cadastroUrl + '/produto/' + id, produto);
  }

  getNcmGenerico(termo: string, page: number, size: number): Observable<any> {
    const params = new HttpParams()
      .set('termo', termo)
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<any>(`${this.configService.settings()?.apiUrl}/fiscal/ncms/busca`, { params });
  }

  getProdutoSimplificado(termo: string, page: number, size: number): Observable<any> {
    const params = new HttpParams()
      .set('termo', termo)
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<any>(`${this.cadastroUrl}/produto/simplificado`, { params });
  }

  getClienteSimplificado(termo: string, page: number, size: number): Observable<any> {
    const params = new HttpParams()
      .set('termo', termo)
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<any>(`${this.cadastroUrl}/clientes/buscar`, { params });
  }

  getServicos(): Observable<any> {
    return this.http.get<any>(`${this.cadastroUrl}/servicos`);
  }

  getServicoSimplificado(termo: string): Observable<any> {
    const params = new HttpParams()
      .set('termo', termo);
    return this.http.get<any>(`${this.cadastroUrl}/servicos/buscar`, { params });
  }

  getServicoById(id: number): Observable<any> {
    return this.http.get<any>(`${this.cadastroUrl}/servicos/${id}`);
  }

  createServico(servico: any): Observable<any> {
    return this.http.post<any>(`${this.cadastroUrl}/servicos`, servico);
  }

  updateServico(servico: any): Observable<any> {
    return this.http.put<any>(`${this.cadastroUrl}/servicos`, servico);
  }

  deleteServico(id: number): Observable<void> {
    return this.http.delete<void>(`${this.cadastroUrl}/servicos/${id}`);
  }

  getHistoricoPrecos(produtoId: number): Observable<any> {
    return this.http.get<any>(`${this.cadastroUrl}/historico-precos/produto/${produtoId}`);
  }

  createHistoricoPrecos(historico: any): Observable<any> {
    return this.http.post<any>(`${this.cadastroUrl}/historico-precos`, historico);
  }

  getHistoricoSalarioFuncionario(idFuncionario: number): Observable<FuncionarioHistoricoSalario[]> {
    const params = new HttpParams().set('idFuncionario', idFuncionario.toString());
    return this.http.get<FuncionarioHistoricoSalario[]>(`${this.cadastroUrl}/funcionarios-historico-salario`, { params });
  }

  getControlesProducaoDetalhado(): Observable<ControleProducaoDetalhado[]> {
    return this.http.get<ControleProducaoDetalhado[]>(`${this.configService.settings()?.apiUrl}/producao/controle/detalhado`);
  }

  getHistoricoProducao(idProducao: number): Observable<HistoricoProducao[]> {
    return this.http.get<HistoricoProducao[]>(`${this.configService.settings()?.apiUrl}/producao/historico/producao/${idProducao}`);
  }

  createHistoricoProducao(payload: CriarHistoricoProducao): Observable<HistoricoProducao> {
    return this.http.post<HistoricoProducao>(`${this.configService.settings()?.apiUrl}/producao/historico`, payload);
  }

  getFuncionariosCargos(): Observable<FuncionarioCargoProducao[]> {
    return this.http.get<FuncionarioCargoProducao[]>(`${this.configService.settings()?.apiUrl}/funcionarios/cargos`);
  }

  deleteHistoricoProducao(idHistorico: number): Observable<void> {
    return this.http.delete<void>(`${this.configService.settings()?.apiUrl}/producao/historico/producao/${idHistorico}`);
  }

  createHistoricoSalarioFuncionario(historico: FuncionarioHistoricoSalario): Observable<FuncionarioHistoricoSalario> {
    return this.http.post<FuncionarioHistoricoSalario>(`${this.cadastroUrl}/funcionarios-historico-salario`, historico);
  }

  getCargos(): Observable<Cargo[]> {
    return this.http.get<Cargo[]>(`${this.cadastroUrl}/cargos`);
  }

  getFuncionarios(): Observable<Funcionario[]> {
    return this.http.get<Funcionario[]>(this.funcionarioUrl);
  }

  getFuncionarioById(id: number): Observable<Funcionario> {
    return this.http.get<Funcionario>(`${this.funcionarioUrl}/${id}`);
  }

  createFuncionario(funcionario: Funcionario): Observable<Funcionario> {
    return this.http.post<Funcionario>(this.funcionarioUrl, funcionario);
  }

  updateFuncionario(id: number, funcionario: Funcionario): Observable<Funcionario> {
    return this.http.put<Funcionario>(`${this.funcionarioUrl}/${id}`, funcionario);
  }

  deleteFuncionario(id: number): Observable<void> {
    return this.http.delete<void>(`${this.funcionarioUrl}/${id}`);
  }

  getVendedores(): Observable<VendedorCadastro[]> {
    return this.http.get<any>(`${this.cadastroUrl}/vendedores`).pipe(
      map(r => Array.isArray(r) ? r : (r?.content ?? []))
    );
  }

  getVendedorById(id: number): Observable<VendedorCadastro> {
    return this.http.get<VendedorCadastro>(`${this.cadastroUrl}/vendedores/${id}`);
  }

  createVendedor(vendedor: VendedorCadastro): Observable<VendedorCadastro> {
    return this.http.post<VendedorCadastro>(`${this.cadastroUrl}/vendedores`, vendedor);
  }

  updateVendedor(vendedor: VendedorCadastro): Observable<VendedorCadastro> {
    return this.http.put<VendedorCadastro>(`${this.cadastroUrl}/vendedores`, vendedor);
  }

  deleteVendedor(id: number): Observable<void> {
    return this.http.delete<void>(`${this.cadastroUrl}/vendedores/${id}`);
  }

  getCargoById(id: number): Observable<Cargo> {
    return this.http.get<Cargo>(`${this.cadastroUrl}/cargos/${id}`);
  }

  createCargo(cargo: Cargo): Observable<Cargo> {
    return this.http.post<Cargo>(`${this.cadastroUrl}/cargos`, cargo);
  }

  updateCargo(cargo: Cargo): Observable<Cargo> {
    return this.http.put<Cargo>(`${this.cadastroUrl}/cargos/${cargo.id}`, cargo);
  }

  deleteCargo(id: number): Observable<void> {
    return this.http.delete<void>(`${this.cadastroUrl}/cargos/${id}`);
  }
}
